/**
 * Payroll — consolidated Team-Attendance + Payroll module (v109.9.23).
 *
 * Phase 1 (this file): shell with 6 tabs + per-tab Access config
 *   Attendance · Salary Calc · Salary Advance · Comp Off · Leave Balance · Accounts
 *
 * Access model
 * ------------
 * • Master Admin  — sees & controls every tab.
 * • Users listed in `payroll_config.tab_access[tab]` — MA-equivalent
 *   authority on that tab ONLY. Injected into the user's `modules`
 *   as `payroll` on the backend (`_augment_user_with_dynamic_modules`).
 * • HR Admin — legacy fallback so existing HR workflows keep working
 *   until MA finishes the tab-access rollout.
 *
 * Phase 2+ will fill each tab with the full spec (per-day cube grid,
 * publish workflow, edit-requests, salary formula engine, advances,
 * comp-off approvals, leave-balance accrual, accounts vouchers).
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/GradientCard";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import UserPickerDialog from "../components/UserPickerDialog";
import { toast } from "sonner";
import {
  CalendarCheck, Calculator, Wallet, Coffee, HandCoins, Banknote,
  Settings2, ShieldCheck, Info, Users, X,
} from "lucide-react";

// v109.9.24 — Phase 4/5/6 lean tabs + Phase 2 attendance grid + Phase 5 salary calc
import {
  LeaveBalanceTab, SalaryAdvanceTab, CompOffTab, AccountsTab,
  SalaryCalcTab, AttendanceGridTab,
} from "./PayrollTabs";
import { tabAccessibleFor } from "../lib/modules";

const TABS = [
  { key: "attendance",     label: "Attendance",       icon: CalendarCheck, phase: 2 },
  { key: "salary_calc",    label: "Salary Calc",      icon: Calculator,    phase: 5 },
  { key: "salary_advance", label: "Salary Advance",   icon: Wallet,        phase: 5 },
  { key: "comp_off",       label: "Comp Off",         icon: Coffee,        phase: 6 },
  { key: "leave_balance",  label: "Leave Balance",    icon: HandCoins,     phase: 4 },
  { key: "accounts",       label: "Accounts",         icon: Banknote,      phase: 6 },
];

function ComingSoonTile({ phase, label }) {
  return (
    <div className="rounded-xl border-2 border-dashed border-indigo-200 dark:border-indigo-800 bg-indigo-50/40 dark:bg-indigo-950/20 p-8 text-center" data-testid={`payroll-phase-tile-${phase}`}>
      <Info className="w-8 h-8 mx-auto text-indigo-500 mb-2" />
      <div className="text-[15px] font-extrabold text-indigo-800 dark:text-indigo-200">
        {label} — arriving in Phase {phase}
      </div>
      <div className="text-[12px] text-indigo-700 dark:text-indigo-300 mt-1 max-w-xl mx-auto">
        This tab&apos;s dedicated workflow is being built. Once ready, the tab will surface the full flow (with in-tab actions,
        Universal-Bell hooks, and audit trail) exactly as spec&apos;d.
      </div>
    </div>
  );
}

export default function Payroll() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [accessRole, setAccessRole] = useState(null);
  const [showAccess, setShowAccess] = useState(false);
  const [config, setConfig] = useState(null);
  const [users, setUsers] = useState([]);
  const [pickTab, setPickTab] = useState(null);   // { key, label } while UserPicker is open

  const isMaster = user?.role === "master_admin";

  // Load access role & default tab
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/payroll/access-role");
        if (!cancelled) setAccessRole(data);
      } catch {
        if (!cancelled) setAccessRole({ is_master: false, accessible_tabs: [] });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load full config for MA (for the Tab Access dialog).
  const loadConfig = async () => {
    try {
      const { data } = await api.get("/payroll/config");
      setConfig(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  useEffect(() => {
    if (!isMaster) return;
    loadConfig();
    // Load active-employee list for the tab-access picker.
    (async () => {
      try {
        const { data } = await api.get("/employees");
        setUsers((data || []).map((e) => ({
          id: e.id, name: e.name, email: e.email,
          role: e.role, employee_code: e.employee_code,
          department: e.department,
        })));
      } catch { /* ignore */ }
    })();
  }, [isMaster]);

  const accessibleTabs = useMemo(() => {
    // v109.9.66 — Universal Law: Master Access is the sole source of truth
    // for every module's tab visibility. Master admin sees all; everyone else
    // gets exactly what Admin Hub → Master Access grants.
    return TABS.filter((t) => isMaster || tabAccessibleFor(user, "payroll", t.key));
  }, [isMaster, user]);

  // Deep-link support: ?tab=attendance
  const initialTab = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const q = sp.get("tab");
    if (q && (isMaster || tabAccessibleFor(user, "payroll", q))) return q;
    return accessibleTabs[0]?.key || "attendance";
  }, [location.search, isMaster, accessibleTabs, user]);

  const [tab, setTab] = useState(initialTab);
  useEffect(() => { setTab(initialTab); }, [initialTab]);
  const changeTab = (k) => {
    setTab(k);
    const sp = new URLSearchParams(location.search);
    sp.set("tab", k);
    navigate({ pathname: location.pathname, search: sp.toString() }, { replace: true });
  };

  // ---------------- Tab-Access dialog helpers ----------------
  const applyTabPick = async (uidList) => {
    if (!pickTab || !config) { setPickTab(null); return; }
    const newTA = { ...(config.tab_access || {}) };
    newTA[pickTab.key] = uidList;
    try {
      const { data } = await api.patch("/payroll/config", { tab_access: newTA });
      setConfig(data);
      toast.success(`Access saved for “${pickTab.label}”`);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setPickTab(null); }
  };

  if (!accessRole && !isMaster && accessibleTabs.length === 0) {
    return (
      <div className="p-6 text-center text-slate-400 text-sm">Loading Payroll…</div>
    );
  }
  if (accessibleTabs.length === 0) {
    return (
      <div className="p-6 max-w-xl mx-auto text-center" data-testid="payroll-no-access">
        <ShieldCheck className="w-10 h-10 text-slate-300 mx-auto mb-2" />
        <div className="text-[15px] font-bold text-slate-700">No tab access</div>
        <div className="text-[12px] text-slate-500 mt-1">
          You have not been granted access to any Payroll tab yet. Ask a Master Admin to add you via
          Payroll → Tab Access.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1500px] space-y-5" data-testid="payroll-page">
      {/* ─── Payroll Banner — aligned with Incentive module ─── */}
      <PageHeader
        tone="indigo"
        icon={Banknote}
        eyebrow="Finance · Payroll"
        title="Payroll"
        moduleKey="payroll"
        subtitle="Attendance · Salary · Advances · Comp Off · Leave Balance · Accounts"
        testid="payroll-banner"
        actions={null}
      />

      <Tabs value={tab} onValueChange={changeTab} className="w-full">
        <TabsList
          className="w-full h-auto flex-wrap justify-start gap-1 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-1 rounded-lg"
          data-testid="payroll-tabs-list"
        >
          {accessibleTabs.map(({ key, label, icon: Icon }) => (
            <TabsTrigger
              key={key}
              value={key}
              className="flex items-center gap-1.5 data-[state=active]:bg-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-sm"
              data-testid={`payroll-tab-${key}`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="attendance" className="mt-4">
          <AttendanceGridTab />
        </TabsContent>

        <TabsContent value="salary_calc" className="mt-4">
          <SalaryCalcTab />
        </TabsContent>

        <TabsContent value="salary_advance" className="mt-4">
          <SalaryAdvanceTab />
        </TabsContent>

        <TabsContent value="comp_off" className="mt-4">
          <CompOffTab />
        </TabsContent>

        <TabsContent value="leave_balance" className="mt-4">
          <LeaveBalanceTab />
        </TabsContent>

        <TabsContent value="accounts" className="mt-4">
          <AccountsTab />
        </TabsContent>
      </Tabs>

      {/* ---------------- Tab-Access dialog (MA only) ---------------- */}
      {isMaster && (
        <Dialog open={showAccess} onOpenChange={setShowAccess}>
          <DialogContent className="max-w-2xl" data-testid="payroll-tab-access-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings2 className="w-5 h-5 text-indigo-600" /> Payroll · Tab Access
              </DialogTitle>
            </DialogHeader>
            <div className="text-[12.5px] text-slate-600 mb-2">
              Users assigned to a tab get <strong>Master-Admin-equivalent authority for that tab only</strong>.
              Leave a tab empty to keep it Master-Admin only.
            </div>
            <div className="space-y-2" data-testid="payroll-tab-access-list">
              {TABS.map(({ key, label, icon: Icon }) => {
                const grantees = (config?.tab_access_users?.[key]) || [];
                return (
                  <div key={key} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-800 dark:text-slate-100">
                        <Icon className="w-4 h-4 text-indigo-500" /> {label}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[11px]"
                        onClick={() => setPickTab({ key, label })}
                        data-testid={`payroll-tab-access-pick-${key}`}
                      >
                        <Users className="w-3.5 h-3.5 mr-1" /> Manage users
                      </Button>
                    </div>
                    {grantees.length === 0 ? (
                      <div className="text-[11.5px] text-slate-400 italic">Master Admin only</div>
                    ) : (
                      <div className="flex flex-wrap gap-1.5">
                        {grantees.map((g) => (
                          <Badge key={g.user_id} className="bg-indigo-100 text-indigo-800 border-indigo-300 flex items-center gap-1" data-testid={`payroll-tab-access-user-${key}-${g.user_id}`}>
                            {g.user_name}
                            <button
                              onClick={async () => {
                                const remaining = grantees.filter((x) => x.user_id !== g.user_id).map((x) => x.user_id);
                                const newTA = { ...(config.tab_access || {}) };
                                newTA[key] = remaining;
                                try {
                                  const { data } = await api.patch("/payroll/config", { tab_access: newTA });
                                  setConfig(data);
                                } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
                              }}
                              className="opacity-70 hover:opacity-100"
                              title="Remove"
                              data-testid={`payroll-tab-access-remove-${key}-${g.user_id}`}
                            >
                              <X className="w-2.5 h-2.5" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAccess(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* User picker for a specific tab */}
      {isMaster && pickTab && (
        <UserPickerDialog
          open={!!pickTab}
          onClose={() => setPickTab(null)}
          onDone={(selectedIds) => applyTabPick(selectedIds)}
          users={users}
          initialSelectedIds={(config?.tab_access?.[pickTab.key]) || []}
          title={`Grant access — ${pickTab.label}`}
        />
      )}
    </div>
  );
}
